import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Button, Chip } from '@mui/material';
import * as yup from 'yup';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import ConfirmDialog from 'components/confirm-dialog/ConfirmDialog';
import InsideLoading from 'components/loading/InsideLoading';
import InsideSaving from 'components/loading/InsideSaving';
import NoData from 'components/no-data/NoData';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';
import RecordUsage from 'components/record-shell/RecordUsage';
import { api } from 'services/api';
import history from 'services/history';
import { AccessRule } from 'types/accessRule';
import AccessRuleForm from '../AccessRuleForm';
import { useAccessRuleValidation } from '../validation/accessRuleValidation';

interface AccessRuleWithUsage extends AccessRule {
  usage?: { profiles: number; children: number };
}

const EditAccessRule: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [rule, setRule] = useState<AccessRuleWithUsage | null>(null);
  const [saved, setSaved] = useState<AccessRuleWithUsage | null>(null);
  const [accessRules, setAccessRules] = useState<AccessRule[]>([]);
  const [validation, setValidation, validate] = useAccessRuleValidation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const loadRule = useCallback(() => {
    if (!id) return Promise.resolve();

    return Promise.all([api.get<AccessRuleWithUsage>(`/access-rules/${id}`), api.get('/access-rules')])
      .then(([ruleResponse, rulesResponse]) => {
        setRule(ruleResponse.data);
        setSaved(ruleResponse.data);
        setAccessRules(rulesResponse.data);
      })
      .catch(() => {
        toast.error('Não foi possível carregar a regra de acesso');
        history.push('/access-rules');
      });
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadRule().finally(() => setLoading(false));
  }, [loadRule]);

  function goBack() {
    history.push('/access-rules');
  }

  function handleChange<K extends keyof AccessRule>(index: K, value: AccessRule[K]) {
    setRule(oldValue => (oldValue ? { ...oldValue, [index]: value } : null));
  }

  function handleSubmit() {
    if (!rule) return;

    setSaving(true);

    api
      .put(`/access-rules/${id}`, rule)
      .then(() => loadRule())
      .then(() => toast.success('Regra atualizada'))
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível salvar a regra de acesso');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    if (!rule) return;

    validate(rule)
      .then(() => {
        handleSubmit();
        setValidation({});
      })
      .catch((err: yup.ValidationError) => {
        if (err instanceof yup.ValidationError) {
          setValidation({
            [err.path as string]: err.message,
          });
        }
      });
  }

  function handleDelete() {
    setSaving(true);

    api
      .delete(`/access-rules/${id}`)
      .then(() => {
        toast.success('Regra excluída');
        history.push('/access-rules');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível excluir a regra de acesso');
      })
      .finally(() => {
        setSaving(false);
        setConfirmDialogOpen(false);
      });
  }

  const usage = rule?.usage;
  const inUse = !!usage && usage.profiles + usage.children > 0;

  return (
    <>
      <Appbar title="Regras de acesso" backAction={goBack} />

      {saving && <InsideSaving />}

      {confirmDialogOpen && (
        <ConfirmDialog
          confirmLabel="Excluir"
          destructive
          title="Deseja excluir esta regra?"
          handleNoClick={() => setConfirmDialogOpen(false)}
          handleYesClick={handleDelete}
        />
      )}

      <PageShell>
        <RecordHeader title={saved?.title || 'Regra de acesso'} backAction={goBack}>
          {saved?.name && <Chip size="small" variant="outlined" label={saved.name} />}
        </RecordHeader>

        <WorkArea>
          <ContentPad>
            {loading ? (
              <InsideLoading />
            ) : !rule ? (
              <NoData message="Não foi possível carregar a regra de acesso" />
            ) : (
              <StyledForm onSubmit={handleValidation}>
                <AccessRuleForm
                  accessRules={accessRules}
                  handleChange={handleChange}
                  accessRule={rule}
                  validation={validation}
                />

                {usage && (
                  <RecordUsage
                    title="Onde é usada"
                    items={[
                      { count: usage.profiles, singular: 'perfil', plural: 'perfis' },
                      { count: usage.children, singular: 'regra filha', plural: 'regras filhas' },
                    ]}
                    blockedMessage="Esta regra está em uso e não pode ser excluída. Remova-a dos perfis e das regras filhas antes."
                    freeMessage="Nenhum perfil nem regra filha usa esta regra, então ela pode ser excluída."
                  />
                )}
                <button type="submit" style={{ display: 'none' }} />
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        {!loading && rule && (
          <RecordFooter sx={{ justifyContent: 'space-between' }}>
            <Button color="error" size="small" disabled={saving || inUse} onClick={() => setConfirmDialogOpen(true)}>
              Excluir regra
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
                Salvar alterações
              </Button>
            </div>
          </RecordFooter>
        )}
      </PageShell>
    </>
  );
};

export default EditAccessRule;
